/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Select,
  Box,
  H3,
  MaterialTableWrapper,
  MRT_TableOptions,
  MRT_ColumnDef,
  MRT_Row,
  withThemeProvider,
  OusterProvider,
} from '@ouster/webviz-react-components';
import type { MaterialTableWrapperProps } from '@ouster/webviz-react-components';

import { IconButton, Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { RiDeleteBin5Line, RiMailSendLine } from 'react-icons/ri';

import styles from './Organization.module.css';
import type { UserWithRole, UserRole } from '../../types';
import { ROLES } from '../../utils/constants';

type OrganizationMembersProps = {
  isAdmin: boolean;
  orgUserList: UserWithRole[];
  curUser: UserWithRole;
  isOusterUser: boolean;
  deleteUserHandler: (member: UserWithRole) => void;
  updateUserRoleHandler: (member: UserWithRole, role: UserRole) => void;
  reinviteHandler: (uuid: string) => void;
};

/* 
  cell component for change role permission
*/
type RoleSelectCellProps = {
  isAdmin: boolean;
  curUser: UserWithRole;
  availableRoles:
    | readonly ['ADMIN', 'SUPPORT', 'BASIC']
    | readonly ['ADMIN', 'BASIC'];
  row: MRT_Row<UserWithRole>;
  updateUserRoleHandler: (member: UserWithRole, role: UserRole) => void;
};

const RoleSelectCell = ({
  isAdmin,
  curUser,
  availableRoles,
  row,
  updateUserRoleHandler,
}: RoleSelectCellProps) => {
  const value = row.original.role;
  const { email } = row.original;
  const invitationStatus = row.original.invitation_status;

  const handleChange = useCallback(
    (e) => {
      updateUserRoleHandler(row.original, e.target.value as UserRole);
    },
    [row, updateUserRoleHandler]
  );

  return (
    <OusterProvider>
      <Select
        textTransform="capitalize"
        defaultValue={value}
        isDisabled={
          !isAdmin || curUser?.email === email || invitationStatus === 'CREATED'
        }
        onChange={handleChange}
        data-testid="role-select"
      >
        {availableRoles.map((role) => (
          <option
            key={role}
            value={role}
            data-testid={`role-option-${role.toLowerCase()}`}
          >
            {role}
          </option>
        ))}
      </Select>
    </OusterProvider>
  );
};

/* 
  cell component for action columns: re-invite and delete user
*/

type ActionsCellProps = {
  isAdmin: boolean;
  curUser: UserWithRole;
  row: MRT_Row<UserWithRole>;
  deleteUserHandler: (member: UserWithRole) => void;
  reinviteHandler: (uuid: string) => void;
};
const ActionsCell = ({
  isAdmin,
  curUser,
  row,
  deleteUserHandler,
  reinviteHandler,
}: ActionsCellProps) => (
  <Box
    flexWrap="nowrap"
    display="flex"
    columnGap={2}
    data-testid={`actions-cell-${row.original.email}`}
  >
    <Tooltip
      arrow
      title={
        !isAdmin || curUser?.email === row.original.email ? '' : 'Remove member'
      }
    >
      <span>
        <IconButton
          onClick={() => deleteUserHandler(row.original)}
          disabled={!isAdmin || curUser?.email === row.original.email}
          data-testid="role-delete-member"
          aria-label="Remove member"
        >
          <RiDeleteBin5Line />
        </IconButton>
      </span>
    </Tooltip>
    {row.original.invitation_status === 'CREATED' && (
      <Tooltip arrow title={isAdmin ? 'Reinvite member' : ''}>
        <span>
          <IconButton
            onClick={() =>
              row.original?.invitation_link &&
              reinviteHandler(row.original.invitation_link)
            }
            data-testid="role-reinvite-member"
            aria-label="Reinvite member"
            disabled={!isAdmin}
          >
            <RiMailSendLine />
          </IconButton>
        </span>
      </Tooltip>
    )}
  </Box>
);

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<UserWithRole>>(
    MaterialTableWrapper
  );

export default function OrganizationMembers({
  isAdmin,
  orgUserList,
  curUser,
  isOusterUser,
  deleteUserHandler,
  updateUserRoleHandler,
  reinviteHandler,
}: OrganizationMembersProps) {
  const availableRoles = useMemo(
    () => (isOusterUser ? ROLES : (['ADMIN', 'BASIC'] as const)),
    [isOusterUser]
  );

  const renderRoleCell = useCallback(
    ({ row }: { row: MRT_Row<UserWithRole> }) => (
      <RoleSelectCell
        isAdmin={isAdmin}
        curUser={curUser}
        availableRoles={availableRoles}
        row={row}
        updateUserRoleHandler={updateUserRoleHandler}
      />
    ),
    [isAdmin, curUser, availableRoles, updateUserRoleHandler]
  );

  const columns = useMemo<MRT_ColumnDef<UserWithRole>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell, row }) => {
          const name = cell.getValue();
          const { email } = row.original;
          return name || email;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'role',
        header: 'Role',
        Cell: renderRoleCell,
      },
      {
        header: 'Status',
        accessorFn: (row) =>
          row.invitation_status === 'CREATED' ? 'Invited' : 'Joined',
        Cell: ({ cell }) => cell.getValue(),
      },
    ],
    [renderRoleCell]
  );

  const tableOptions: MRT_TableOptions<UserWithRole> = {
    columns,
    data: orgUserList,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'], left: ['name'] },
      showGlobalFilter: true,
    },
    enableRowActions: true,
    positionGlobalFilter: 'left',
    enableSorting: true,
    enableToolbarInternalActions: false,
    positionActionsColumn: 'last',
    getRowId: (originalRow) => originalRow.email,
    renderRowActions: ({ row }) => (
      <OusterProvider>
        <ActionsCell
          isAdmin={isAdmin}
          curUser={curUser}
          row={row}
          deleteUserHandler={deleteUserHandler}
          reinviteHandler={reinviteHandler}
        />
      </OusterProvider>
    ),
  };

  return (
    <div
      className={styles['section-container']}
      data-testid="org-members-section"
    >
      <H3
        fontSize={16}
        textTransform="uppercase"
        className={styles.heading}
        data-testid="org-members-heading"
      >
        Organization Members
      </H3>
      <ThemedMaterialTableWrapper {...tableOptions} />
    </div>
  );
}
